/**
 * Resolves the active SEPA mandate out of a `sepa_mandates` collection.
 *
 * A customer should only ever have one active mandate, but nothing in the schema enforces that and
 * historic write paths left extra active rows behind. Picking the highest id keeps this in step with
 * the backend, which resolves the active mandate the same way - taking whichever row happened to come
 * first meant showing a superseded IBAN.
 *
 * @param {Array<{id: number, is_active: number, iban: ?string, bic: ?string, account_owner: ?string}>} mandates
 * @returns {?object} the active mandate, or null when there is none
 */
export function activeSepaMandateOf (mandates) {
    return (mandates ?? [])
        .filter((sepaMandate) => Number(sepaMandate.is_active) === 1)
        .sort((a, b) => Number(b.id) - Number(a.id))[0] ?? null
}

/**
 * @param {{sepa_mandates?: Array<object>}} customer
 * @returns {?object} the customer's active mandate, or null when there is none
 */
export function activeSepaMandate (customer) {
    return activeSepaMandateOf(customer?.sepa_mandates)
}

/**
 * Bank details of the customer's active mandate, blank-filled for form binding.
 *
 * @param {object} customer
 * @returns {{iban: string, bic: string, accountOwner: string}}
 */
export function customerBankDetails (customer) {
    const mandate = activeSepaMandate(customer) ?? {}

    return {
        iban: mandate.iban ?? '',
        bic: mandate.bic ?? '',
        accountOwner: mandate.account_owner ?? ''
    }
}
